/**
 * Central feature flag registry for path-web.
 *
 * This module is the single source of truth for:
 *  - The canonical list of flag names (FeatureFlagName union type)
 *  - Default values for every flag
 *  - Environment variable (VITE_FF_*) and local-config overrides
 *
 * Priority (highest wins):
 *   1. VITE_FF_<FLAG_UPPER> environment variable (e.g. VITE_FF_AI_CHAT=true)
 *   2. Local config JSON at /feature-flags.local.json (git-ignored, dev overrides)
 *   3. Server-side flags from GET /api/v1/feature-flags (loaded by FeatureFlagProvider)
 *   4. Coded defaults below
 *
 * Usage:
 *   // In a component or hook:
 *   import { useFeatureFlag } from '@/hooks/useFeatureFlag';
 *   const enabled = useFeatureFlag('AiChat');
 *
 *   // Gate a subtree:
 *   <FeatureGate flag="AiChat"><SophiaChat /></FeatureGate>
 *
 * Adding a new flag:
 *   1. Add the flag name to the FeatureFlagName union type below.
 *   2. Set its default in FLAG_DEFAULTS.
 *   3. Add the same flag to the backend InMemoryFeatureFlagService.Flags constants
 *      and to appsettings.json.
 *   4. Optionally add a VITE_FF_<NAME> entry to .env.local to override during dev.
 */

// ─── Canonical flag registry ──────────────────────────────────────────────────

/**
 * All known feature flag names. Add new flags here as you introduce them.
 * The name must match the key returned by GET /api/v1/feature-flags (case-insensitive merge).
 */
export type FeatureFlagName =
  // Product feature flags
  | 'CoBorrowerFlow'    // gates co-borrower UI when built
  | 'AiChat'            // gates Sophia AI chat
  | 'DocumentUpload'    // gates document upload
  | 'I18n'              // gates internationalization

  // Infrastructure / internal flags
  | 'VestaSubmission'
  | 'SqlAzureProbe'
  | 'DarkMode'
  // NP-408: Income Verification + Conditions tabs hidden for v1.0 — flags
  // (not deletion) so a future release can re-enable them. Names match the
  // server's GET /api/v1/feature-flags keys (IncomeVerificationPage /
  // ConditionsPage) so the API response controls the tabs directly.
  | 'IncomeVerificationPage'
  | 'ConditionsPage'
  // NP-553: surfaces showing placeholder or hardcoded content, hidden until the
  // data behind them is real. Same treatment as NP-408 — flagged, never deleted.
  | 'MyHomePage'            // My Home tab, route and the links into it
  | 'ESignatureFields'      // Review → Disclosures e-signature name/date rows
  | 'SophiaDocumentsCta'    // "Talk to Sophia first" on the Documents empty state
  | 'DashboardPendingItems' // the hardcoded Pending items card
  | 'DashboardEditPropertyAddress' // post-submission property edit (its save fails)
  // NP-350: the standalone Purchase/Refinance calculator on the Dashboard, hidden
  // while Legal reviews its content. Not a placeholder like the NP-553 set — the
  // calculator works; it just cannot face borrowers yet, so it is flagged rather
  // than deleted and NP-673's accuracy work stays in the tree. Gating this one
  // name covers the embedded explorer, the /calculator* routes and the links into
  // them, so there is no second switch to forget.
  | 'DashboardCalculator'
  // NP-754: the Payments tab, route and the links into it. Its pay-now, autopay and
  // history are mock or incomplete, so it is hidden NP-553-style — flagged, never
  // deleted — and re-enabled by flipping this flag once the feature is built.
  | 'PaymentsPage'
  // Tasks tab, ported from AFN.BorrowerPortal.Web. On by default — the list is
  // mocked client-side (afn-path-api has no Tasks endpoint yet, per
  // docs/API-MIGRATION-SPEC.md's Phase-2 note) but uploads are real, so the tab
  // ships visible. Kept as a flag for an ops kill-switch, not to hide it.
  | 'TasksPage'
  // NOTE: 'VestaDirectWizard' was REMOVED (WS9-1 Phase 6, 2026-07-13) — the
  // Vesta-direct wizard is the permanent, unconditional create/save/submit
  // spine (docs/API-MIGRATION-SPEC.md §6). Do not reintroduce a flag for it.

  // Legacy aliases kept for backwards compatibility
  | 'SophiaChat';       // alias for AiChat (older code may reference this)

// ─── Coded defaults ───────────────────────────────────────────────────────────

/**
 * Default values used until the API responds and when env var / local config
 * doesn't specify a value.
 *
 * Keep these in sync with appsettings.json FeatureFlags section on the backend.
 */
export const FLAG_DEFAULTS: Record<FeatureFlagName, boolean> = {
  // Product flags
  CoBorrowerFlow: false,  // off until co-borrower UI is built
  AiChat: true,           // Sophia enabled by default
  DocumentUpload: true,   // document upload enabled by default
  I18n: false,            // i18n off until fully rolled out

  // Infrastructure
  VestaSubmission: true,
  SqlAzureProbe: true,
  DarkMode: false,
  // NP-408: hidden for v1.0, re-enable in a future release (flip to true or
  // set via server flags / VITE_FF_* env / local config).
  IncomeVerificationPage: false,
  ConditionsPage: false,
  // NP-553: default OFF so an environment that has not been configured hides
  // them, which is the state the ACs describe. Dev turns them back on through
  // the server flags or a local override — no rebuild, no redeploy.
  MyHomePage: false,
  ESignatureFields: false,
  SophiaDocumentsCta: false,
  DashboardPendingItems: false,
  DashboardEditPropertyAddress: false,
  // NP-350: default OFF so Staging and Production hide the calculator the moment
  // this ships, with no config step. Legal's re-enable is a flag flip — the server
  // flag (GET /api/v1/feature-flags) or an App Service
  // FEATURE_FLAG_DASHBOARDCALCULATOR=true — never a rebuild. VITE_FF_* is
  // deliberately not wired for it: Vite bakes those at build time, which would
  // make re-enablement a redeploy and miss the point of AC-5.
  //
  // Cadence, since the header above overstates it (Copilot review, PR #342): the
  // per-route refresh is INERT in this app. FeatureFlagProvider is mounted above
  // BrowserRouter, so NavigationRefresher's useInRouterContext() is false and it
  // renders null — see App.tsx's RCA-fix note. Flags land on mount and every five
  // minutes, nothing more. So an already-open session picks up Legal's re-enable
  // within five minutes, and a fresh load gets it immediately.
  DashboardCalculator: false,
  // NP-754: default OFF so every environment hides Payments without a config step.
  PaymentsPage: false,
  TasksPage: true,

  // Legacy
  SophiaChat: true,       // same default as AiChat
};

// ─── VITE_FF_* environment variable overrides ─────────────────────────────────

/**
 * Reads VITE_FF_* env vars at bundle time and returns a partial override record.
 *
 * Convention:  VITE_FF_<FLAG_NAME_UPPERCASE>=true|false
 * Examples:
 *   VITE_FF_AI_CHAT=true          → AiChat = true
 *   VITE_FF_COBORROWERFLOW=false  → CoBorrowerFlow = false
 *   VITE_FF_I18N=true             → I18n = true
 *
 * The env var name is derived by uppercasing the flag name and (optionally)
 * inserting underscores before capital letters for readability.
 */
function readEnvOverrides(): Partial<Record<string, boolean>> {
  const overrides: Partial<Record<string, boolean>> = {};

  // Map of env var suffix → flag name
  const envMap: Record<string, FeatureFlagName> = {
    COBORROWERFLOW:  'CoBorrowerFlow',
    COBORROWER_FLOW: 'CoBorrowerFlow',
    AICHAT:          'AiChat',
    AI_CHAT:         'AiChat',
    SOPHIACHAT:      'SophiaChat',
    SOPHIA_CHAT:     'SophiaChat',
    DOCUMENTUPLOAD:  'DocumentUpload',
    DOCUMENT_UPLOAD: 'DocumentUpload',
    I18N:            'I18n',
    VESTASUBMISSION: 'VestaSubmission',
    VESTA_SUBMISSION:'VestaSubmission',
    SQLAZUREPROBE:   'SqlAzureProbe',
    SQL_AZURE_PROBE: 'SqlAzureProbe',
    DARKMODE:        'DarkMode',
    DARK_MODE:       'DarkMode',
    INCOMEVERIFICATIONPAGE:   'IncomeVerificationPage',
    INCOME_VERIFICATION_PAGE: 'IncomeVerificationPage',
    CONDITIONSPAGE:   'ConditionsPage',
    CONDITIONS_PAGE:  'ConditionsPage',
  };

  for (const [envSuffix, flagName] of Object.entries(envMap)) {
    const key = `VITE_FF_${envSuffix}` as keyof ImportMeta['env'];
    const raw = import.meta.env[key];
    if (raw === 'true' || raw === '1')  overrides[flagName] = true;
    if (raw === 'false' || raw === '0') overrides[flagName] = false;
  }

  return overrides;
}

/**
 * Merged defaults with VITE_FF_* environment variable overrides applied.
 * This is used as the initial flag state before the API responds.
 */
export const INITIAL_FLAGS: Record<FeatureFlagName, boolean> = {
  ...FLAG_DEFAULTS,
  ...readEnvOverrides(),
} as Record<FeatureFlagName, boolean>;

// ─── Helper utilities ─────────────────────────────────────────────────────────

/**
 * Checks if a flag is enabled given a flags record.
 * Falls back to FLAG_DEFAULTS for unknown or missing flags.
 */
export function isFlagEnabled(
  flags: Record<string, boolean>,
  flag: FeatureFlagName,
): boolean {
  if (flag in flags) return flags[flag];
  return FLAG_DEFAULTS[flag] ?? false;
}
